"use client";

import React, { useState, useEffect } from "react";
import Icon from "@/components/ui/Icon";

interface TableOfContentsProps {
  headings: { id: string; title: string }[];
}

export default function BlogTableOfContents({ headings }: TableOfContentsProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [activeId, setActiveId] = useState<string>(headings?.[0]?.id || "");

  // Scrollspy: Track active heading currently visible in viewport
  useEffect(() => {
    if (!headings || headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-90px 0% -65% 0%",
        threshold: 0.1,
      }
    );

    headings.forEach((heading) => {
      const el = document.getElementById(heading.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [headings, activeId]);

  if (!headings || headings.length < 2) return null;

  const scrollToHeading = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    setActiveId(id);
    const element = document.getElementById(id);
    if (element) {
      const navOffset = 95;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: Math.max(0, elementPosition - navOffset),
        behavior: "smooth",
      });
      // Update hash without jumping
      if (window.history && window.history.pushState) {
        window.history.pushState(null, "", `#${id}`);
      }
    }
  };

  return (
    <nav
      aria-label="Table of Contents"
      className="rounded-2xl border border-outline-variant/30 bg-surface-container-low/80 dark:bg-[#25201D]/90 backdrop-blur-md shadow-sm transition-all duration-300 overflow-hidden"
    >
      {/* Header bar with collapsible toggle */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between p-4 sm:p-4.5 cursor-pointer select-none border-b border-outline-variant/20 hover:bg-surface-container/50 transition-colors"
      >
        <div className="flex items-center gap-2.5 text-primary font-bold text-xs uppercase tracking-wider">
          <Icon name="layers" size={17} className="text-primary" />
          <span>In This Article</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-mono font-bold">
            {headings.length}
          </span>
        </div>
        <button
          type="button"
          aria-label={isOpen ? "Collapse Table of Contents" : "Expand Table of Contents"}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-on-surface-variant hover:text-primary hover:bg-surface transition-colors"
        >
          <Icon
            name="expand_more"
            size={20}
            className={`transition-transform duration-300 ${isOpen ? "rotate-180" : "rotate-0"}`}
          />
        </button>
      </div>

      {/* Collapsible Headings List */}
      {isOpen && (
        <div className="p-3 sm:p-4 max-h-[calc(100vh-14rem)] overflow-y-auto overscroll-contain space-y-1">
          {headings.map((item, index) => {
            const isActive = activeId === item.id;
            const numberLabel = String(index + 1).padStart(2, "0");

            return (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => scrollToHeading(e, item.id)}
                className={`group flex items-start gap-2.5 px-3 py-2 rounded-xl text-xs sm:text-[13px] leading-snug transition-all duration-200 ${
                  isActive
                    ? "bg-primary/15 text-primary font-bold shadow-2xs border-l-3 border-primary pl-2.5"
                    : "text-on-surface-variant hover:text-on-surface hover:bg-surface-container/60 font-medium"
                }`}
              >
                <span
                  className={`font-mono text-[11px] shrink-0 mt-0.5 transition-colors ${
                    isActive ? "text-primary font-bold" : "text-on-surface-variant/60 group-hover:text-primary"
                  }`}
                >
                  {numberLabel}.
                </span>
                <span className="line-clamp-2">{item.title}</span>
              </a>
            );
          })}
        </div>
      )}
    </nav>
  );
}
